import _get from "lodash/get";
import _invert from "lodash/invert";
import _reduce from "lodash/reduce";
import _size from "lodash/size";
import {
    Realm,
    CART_ITEM_HANDSET_TYPES,
    CART_ITEM_SUBSCRIPTION_TYPES,
    CART_ITEM_TYPE,
    CartItemTypeValues,
    COMMITMENT_UPFRONT_TYPE,
    CUSTOMER_TYPE,
    DELIVERY,
    DELIVERY_TYPE,
    DeliveryType,
    FINANCING_TYPE,
    FinancingType,
    GA_TEXT_CONSTS as TEXT,
    PAYMENT_METHOD,
    PaymentMethodKey,
    REALM,
} from "../../../../constants";
import { GAProduct, ICartItem } from "../../../../models";
import { getRealm, isPersonalDetails, State as AppState } from "../../app/state/selectors";
import { getExternalShowB2BFamilyOffer } from "../../checkout/state/selectors/accountSelectors";
import { isFlexLease as isFlexLeaseSelector } from "../../checkout/state/selectors/cartItemSelectors";
import { getFlattenedCartContent, State as CartState } from "../../checkout/state/selectors/cartSelectors";
import {
    getSubscriptionChangeType,
    getSubscriptionPreviousServiceProvider,
    isMultipleSubscriptions,
} from "../../checkout/state/selectors/combinedSelectors";
import {
    getDeliveryInfoCode,
    getDeliveryInfoType,
    State as DeliveryState,
} from "../../checkout/state/selectors/deliveryInfoSelectors";
import { getPaymentSelectedType, State as PaymentState } from "../../checkout/state/selectors/paymentSelectors";
import {
    getPhoneNumberCheckType,
    State as PhoneNumberState,
} from "../../checkout/state/selectors/phoneNumberSelectors";
import {
    getInitiatedByWhenReSwitch,
    getSubscriptionFinancingCode,
    getSubscriptionFinancingDuration,
    getSubscriptionFinancingType,
    getSubscriptions,
    getSubscriptionSize,
    getTotalRemainingMonths,
    hasSubscriptionDataUnlimited as hasSubscriptionDataUnlimitedSelector,
    isLegalSubscription,
    isReSwitch as isReSwitchSelector,
    isSubscription10PercentDiscount,
    isSubscriptionBroadband as isSubscriptionBroadbandSelector,
    isSubscriptionPrepaid as isSubscriptionPrepaidSelector,
    isSubscriptionSimDeal as isSubscriptionSimDealSelector,
    isCatalogListingOk as isCatalogListingOkSelector,
    isProductUserDifferentThanLegalOwner as isProductUserDifferentThanLegalOwnerSelector,
    State as SubscriptionState,
    isTeliaTryggSubscription,
} from "../../checkout/state/selectors/subscriptionsSelectors";
import {
    getAcquisitionOrRetention,
    getCategory,
    getConvergence,
    getPriceIncVatForType,
    getProductId,
    getProductName,
    State as TrackingState,
} from "../../tracking-common/selectors";

export type GaProductsState = CartState & AppState & TrackingState & DeliveryState;
export type State = GaProductsState & PaymentState & PhoneNumberState & SubscriptionState;

export const getGaSalesType: (state: PhoneNumberState, subscriptionId?: string) => string | undefined = (
    state,
    subscriptionId,
) => {
    const customerType = getPhoneNumberCheckType(state, subscriptionId);
    switch (customerType) {
        case CUSTOMER_TYPE.NEW:
            return TEXT.SALES_TYPE.NEW_NUMBER;
        case CUSTOMER_TYPE.EXISTING:
            return TEXT.SALES_TYPE.EXISTING; // TODO: Differentiate upgrade, downgrade and prolonging
        case CUSTOMER_TYPE.PORT_IN:
            return TEXT.SALES_TYPE.PORT_IN;
        default:
            return customerType;
    }
};

export const getGaSubscriptionChangeType: (state: PhoneNumberState, subscriptionId?: string) => string | undefined = (
    state,
    subscriptionId,
) => {
    const gaSalesType = getGaSalesType(state, subscriptionId);
    if (gaSalesType === TEXT.SALES_TYPE.EXISTING) {
        return getSubscriptionChangeType(state, subscriptionId);
    } else {
        return gaSalesType;
    }
};

const getGaInitiatedByWhenReswitch: (state: SubscriptionState, subscriptionId?: string) => string | undefined = (
    state,
    subscriptionId,
) => {
    if (!isReSwitchSelector(state, subscriptionId)) return undefined;

    switch (getInitiatedByWhenReSwitch(state, subscriptionId)) {
        case `app`:
            return `App`;
        case `minside`:
            return `MinSide`;
        default:
            return `WebShop`;
    }
};

const getGaVariant: (state: SubscriptionState, product: ICartItem) => string | undefined = (state, product) => {
    if (product.type === CART_ITEM_TYPE.SUBSCRIPTION) {
        const hasSubscriptionDataUnlimited = hasSubscriptionDataUnlimitedSelector(state, product.bundleId);
        if (hasSubscriptionDataUnlimited) {
            return "unlimited";
        }

        const subscriptionAmount = _get(getSubscriptionSize(state, product.bundleId), "amount");
        const subscriptionUnit = _get(getSubscriptionSize(state, product.bundleId), "unit");
        return `${subscriptionAmount} ${subscriptionUnit}`;
    }

    const sizeMatch = product.model?.match(new RegExp("\\d+\\s?GB"));
    if (sizeMatch) {
        let sizeString = sizeMatch[0];
        if (sizeString.indexOf(" GB") === -1) {
            sizeString = sizeString.replace("GB", " GB");
        }
        return `${product.color},${sizeString}`;
    }
    return product.color;
};

const getGaDeliveryType: (state: DeliveryState) => DeliveryType | string = (state) => {
    const deliveryType = getDeliveryInfoType(state);
    return (deliveryType && DELIVERY[deliveryType]) || `Unknown (${deliveryType})`;
};

const getGaPickupStore: (state: DeliveryState) => string | undefined = (state) => {
    const deliveryInfoType = getDeliveryInfoType(state);
    if (_get(DELIVERY_TYPE, `${deliveryInfoType}`) === DELIVERY_TYPE.PICK_UP_IN_SHOP) {
        return getDeliveryInfoCode(state);
    }
};

const getGaBrand: (product: ICartItem) => string = (product) => product.brand || TEXT.BRAND;

export const getGaSubscriptionType: (
    financing: FinancingType | undefined,
    isSubscriptionPrepaid: boolean,
    isSubscriptionBroadband: boolean,
    isSubscriptionSimDeal: boolean,
    isRingtime: boolean,
    isSecurityService: boolean,
) => string = (
    financing,
    isSubscriptionPrepaid,
    isSubscriptionBroadband,
    isSubscriptionSimDeal,
    isRingtime,
    isSecurityService,
) => {
    if (isSecurityService) {
        return TEXT.SUBSCRIPTION_TYPE.SERVICE;
    }
    if (isRingtime) {
        return TEXT.SUBSCRIPTION_TYPE.RINGTIME;
    }
    if (isSubscriptionSimDeal) {
        return TEXT.SUBSCRIPTION_TYPE.SIM_DEAL;
    }
    if (isSubscriptionBroadband) {
        return TEXT.SUBSCRIPTION_TYPE.MBB;
    }
    if (isSubscriptionPrepaid) {
        return TEXT.SUBSCRIPTION_TYPE.SIM_ONLY;
    }

    switch (financing) {
        case FINANCING_TYPE.SWITCH:
            return TEXT.SUBSCRIPTION_TYPE.SWITCH;
        case FINANCING_TYPE.LEASE:
            return TEXT.SUBSCRIPTION_TYPE.COMMITMENT;
        case FINANCING_TYPE.RESWITCH:
            return TEXT.SUBSCRIPTION_TYPE.RESWITCH;
        default:
            return TEXT.SUBSCRIPTION_TYPE.SIM_ONLY;
    }
};

const getGaSubscriptionTypeForId: (state: SubscriptionState, subscriptionId?: string) => string = (
    state,
    subscriptionId,
) => {
    const financing = getSubscriptionFinancingType(state, subscriptionId);
    const isSubscriptionPrepaid = isSubscriptionPrepaidSelector(state, subscriptionId);
    const isSubscriptionBroadband = isSubscriptionBroadbandSelector(state, subscriptionId);
    const isSubscriptionSimDeal = isSubscriptionSimDealSelector(state, subscriptionId);
    const isRingtime = isSubscription10PercentDiscount(state, subscriptionId);
    const isSecurityService = isTeliaTryggSubscription(state, subscriptionId); // TODO: add SKY later

    return getGaSubscriptionType(
        financing,
        isSubscriptionPrepaid,
        isSubscriptionBroadband,
        isSubscriptionSimDeal,
        isRingtime,
        isSecurityService,
    );
};

export const getRingtimeTypeForId: (state: SubscriptionState, subscriptionId?: string) => string | undefined = (
    state,
    subscriptionId,
) => {
    const has10PercentDiscount = isSubscription10PercentDiscount(state, subscriptionId);

    if (has10PercentDiscount) {
        return "avtaletid 10";
    }
};

export const getIsCatalogListingOkForId: (state: SubscriptionState, subscriptionId?: string) => string | undefined = (
    state,
    subscriptionId,
) => {
    const isCatalogListingOk = isCatalogListingOkSelector(state, subscriptionId);
    return isCatalogListingOk ? "FALSE" : "TRUE";
};

export const getUserIsNotSameAsLegalForId: (state: SubscriptionState, subscriptionId?: string) => string | undefined = (
    state,
    subscriptionId,
) => {
    const isProductUserDifferentThanLegalOwner = isProductUserDifferentThanLegalOwnerSelector(state, subscriptionId);
    return isProductUserDifferentThanLegalOwner ? "FALSE" : "TRUE";
};

const getGaUpfrontFinancing: (state: PaymentState) => string = (state) => {
    const paymentType = getPaymentSelectedType(state);
    const gaPaymentType = paymentType && (_invert(PAYMENT_METHOD)[paymentType] as PaymentMethodKey);

    return (gaPaymentType && TEXT.FINANCING_TYPE[gaPaymentType]) || TEXT.FINANCING_TYPE.UPFRONT;
};

const getGaFinancing: (state: SubscriptionState, product: ICartItem) => string = (state, product) => {
    // billing | firstbill | upfront | upfront Visa | upfront Vipps | SVITSJ | re-SVITSJ | Telia financing12 | Telia financing24 | Flexlease12 | Flexlease24
    // 1) Subscription: Billing
    // 2) With bundleId: Need to figure out financing and derive method thereafter
    // 3) Assume upfront if nothing else (Upfront Mastercard|Upfront Visa|Vipps)

    if (CART_ITEM_SUBSCRIPTION_TYPES.includes(product.type)) {
        return TEXT.FINANCING_TYPE.BILLING;
    }

    if (product.bundleId) {
        if (isReSwitchSelector(state, product.bundleId)) return TEXT.FINANCING_TYPE.RESWITCH;

        const financing = getSubscriptionFinancingType(state, product.bundleId);

        if (financing === FINANCING_TYPE.SWITCH) {
            return TEXT.FINANCING_TYPE.SWITCH;
        } else if (financing === FINANCING_TYPE.LEASE) {
            const code = getSubscriptionFinancingCode(state, product.bundleId);

            // For lease with code 'now', item will be paid for upfront
            if (code === COMMITMENT_UPFRONT_TYPE) {
                return getGaUpfrontFinancing(state);
            }

            const duration = getSubscriptionFinancingDuration(state, product.bundleId);

            const isFlexLease = isFlexLeaseSelector(state, product.bundleId);
            if (isFlexLease) {
                return `${TEXT.FINANCING_TYPE.FLEX_LEASE}${duration}`;
            }

            return `${TEXT.FINANCING_TYPE.LEASE}${duration}`;
        }
    }

    return getGaUpfrontFinancing(state);
};

export const getGaComboSaleName: (gaProducts: GAProduct[]) => string = (gaProducts) =>
    gaProducts
        .map((item) => (item.quantity > 1 ? item.quantity + "* " + item.name : item.name))
        .filter((name) => !!name)
        .join(",");

export const getGaRealm: (gaProducts: GAProduct[], realm: Realm) => string = (gaProducts, realm) => {
    switch (realm) {
        case REALM.B2B:
            return "Enterprise";
        case REALM.B2C:
            if (gaProducts.find((item) => item.dimension78 === TEXT.SUBSCRIPTION_TYPE.MBB)) {
                return "Families";
            }
            return "Individuals";
        default:
            return "Unknown realm";
    }
};

export const getGaProducts: (state: GaProductsState) => GAProduct[] = (state) => {
    const realm = getRealm(state);
    const gaProducts = getFlattenedCartContent(state)
        .filter(
            (product) =>
                !product.name.startsWith("Beholder dagens abonnement") &&
                !product.name.startsWith("Bytter fra") &&
                !product.name.startsWith("Eksisterende Telia"),
        )
        .map((product) => {
            let gaProduct: GAProduct = {
                name: getProductName(product),
                id: getProductId(product),
                price: getPriceIncVatForType(state, product, realm),
                brand: getGaBrand(product),
                category: getCategory(state, product),
                quantity: _get(product, "quantity.value", 1),
            };

            const cartItemTypes: CartItemTypeValues[] = [
                CART_ITEM_TYPE.SUBSCRIPTION,
                CART_ITEM_TYPE.HANDSET,
                CART_ITEM_TYPE.HANDSET_PREOWNED,
                CART_ITEM_TYPE.MODEM,
                CART_ITEM_TYPE.ACCESSORY,
                CART_ITEM_TYPE.WEARABLE,
                CART_ITEM_TYPE.CONNECTED_DEVICE,
                CART_ITEM_TYPE.TABLET,
            ];
            if (cartItemTypes.includes(product.type)) {
                gaProduct = {
                    ...gaProduct,
                    variant: getGaVariant(state, product),
                    dimension1: getGaFinancing(state, product), // billing | firstbill | upfront | upfront Visa | upfront Vipps | SVITSJ | re-SVITSJ | Telia financing12 | Telia financing24| Flexlease12 | Flexlease24
                    dimension75: getGaDeliveryType(state),
                    dimension76: getGaPickupStore(state), // Pickup-store if available
                };
            }

            if (CART_ITEM_HANDSET_TYPES.includes(product.type) && isReSwitchSelector(state, product.bundleId)) {
                const dimension84 = getGaInitiatedByWhenReswitch(state, product.bundleId); //initiatedBy=[iOs, Android, MinSide, WebShop] when doing re-svitsj
                const dimension81 = getTotalRemainingMonths(state, product.bundleId);
                gaProduct = {
                    ...gaProduct,
                    dimension84,
                    dimension81,
                };
            }

            if (CART_ITEM_SUBSCRIPTION_TYPES.includes(product.type)) {
                if (isMultipleSubscriptions(state) && isLegalSubscription(state, product.bundleId)) {
                    const dimension86 = "TRUE";
                    const dimension87 = _size(getSubscriptions(state)) || 1; // set it to default 1 - should not be needed.
                    gaProduct = {
                        ...gaProduct,
                        dimension86,
                        dimension87,
                    };
                }

                const dimension3 = getAcquisitionOrRetention(state, product.bundleId); // "Aquisition | Retention",
                const dimension73 = getGaSubscriptionChangeType(state, product.bundleId); // "New number | Portin | Upsell | Prolonging | Downsell"
                const dimension79 = getSubscriptionPreviousServiceProvider(state, product.bundleId); // Service Provider if port_in
                const dimension90 = getConvergence(state);
                const dimension96 = getRingtimeTypeForId(state, product.bundleId);
                const dimension108 = getExternalShowB2BFamilyOffer(state) ? "B2B_FAMILY_OFFER" : "false";

                gaProduct = {
                    ...gaProduct,
                    dimension3,
                    dimension73: dimension73 === null ? undefined : dimension73,
                    dimension78: getGaSubscriptionTypeForId(state, product.bundleId), // SVITSJ | re-SVITSJ | Commitment | Sim only | Sim deal | MBB | Prepaid | Service
                    dimension79,
                    dimension90,
                    dimension96,
                    dimension108,
                };
                if (!isPersonalDetails(state)) {
                    const dimension102 = getIsCatalogListingOkForId(state, product.bundleId);
                    const dimension103 = getUserIsNotSameAsLegalForId(state, product.bundleId);
                    gaProduct = {
                        ...gaProduct,
                        dimension102,
                        dimension103,
                    };
                }
            } else if (product.type === CART_ITEM_TYPE.SPECIALPRODUCT) {
                gaProduct = {
                    ...gaProduct,
                    dimension78: TEXT.SUBSCRIPTION_TYPE.SIM_ONLY,
                };
            } else if (CART_ITEM_HANDSET_TYPES.includes(product.type)) {
                gaProduct = {
                    ...gaProduct,
                    dimension89: product.subtype === "PREOWNED" ? "Pre-owned" : "New",
                };
            }

            if (product.isWebDeal) {
                gaProduct = {
                    ...gaProduct,
                    dimension82: "Yes", // set to "Yes" if this is a web deal or if it is a discount on a hw-product
                };
            }

            return gaProduct;
        });

    // Calculate ComboSale category dimension on outside as it's equal for all
    const comboSaleName = getGaComboSaleName(gaProducts);

    // Calculate GA realm dimension
    const gaRealm = getGaRealm(gaProducts, realm);

    // Append some custom dimensions to all ga products
    gaProducts.forEach((product) => {
        product.dimension74 = comboSaleName;
        product.dimension80 = gaRealm;
    });

    return gaProducts;
};

type RevenueProps = { price: number; quantity: number };
export const getGaRevenue: (products: RevenueProps[]) => number = (products) =>
    _reduce(
        products,
        (totalPrice, product) => {
            const totalProductPrice = product.price * product.quantity;
            return totalPrice + totalProductPrice;
        },
        0,
    );

// Optimize
export const getComboProductName: (state: CartState) => string = (state) =>
    getFlattenedCartContent(state)
        .map((item) => (_get(item, "quantity.value", 1) > 1 ? item.quantity.value + "* " + item.name : item.name))
        .filter((name) => !!name)
        .join(",");
